import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { getAdminSection } from '../../admin/adminSections';
import {
  buildProductImageUrl,
  listAdminProducts,
} from '../../services/adminProductService';
import {
  createAdminVariant,
  deleteAdminVariant,
  buildVariantImageUrl,
  listAdminVariants,
  updateAdminVariant,
} from '../../services/adminVariantService';

const initialFormValues = {
  product_id: '',
  variant_name: '',
  sku: '',
  price: '',
  mrp: '',
  weight: '',
  status: '1',
};

function normalizeVariant(item) {
  return {
    variant_id: item.variant_id ?? item.id ?? item.variantId ?? '',
    product_id: item.product_id ?? item.productId ?? '',
    variant_name: item.variant_name ?? item.variantName ?? '',
    sku: item.sku ?? '',
    price: item.price ?? '',
    mrp: item.mrp ?? '',
    weight: item.weight ?? '',
    image: item.image ?? '',
    status: String(item.status ?? '1'),
  };
}

function normalizeProduct(item) {
  return {
    product_id: item.product_id ?? item.id ?? item.productId ?? '',
    name: item.name ?? '',
    image: item.image ?? '',
  };
}

export default function VariantsPage() {
  const section = getAdminSection('variants');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formValues, setFormValues] = useState(initialFormValues);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageName, setSelectedImageName] = useState('');
  const [existingImageLabel, setExistingImageLabel] = useState('');

  const visibleColumns = useMemo(() => {
    const fields = [
      { key: 'product_id', label: 'Product ID' },
      { key: 'variant_name', label: 'Variant Name' },
      { key: 'sku', label: 'SKU' },
      { key: 'price', label: 'Price' },
      { key: 'weight', label: 'Weight' },
      { key: 'status', label: 'Status' },
    ];

    if (isMobile) {
      return fields.slice(0, 2);
    }

    if (isTablet) {
      return fields.slice(0, 4);
    }

    return fields;
  }, [isMobile, isTablet]);

  const selectedProduct =
    products.find(
      (product) => String(product.product_id) === String(formValues.product_id),
    ) || null;

  const headerMetrics = [
    { label: 'Total variants', value: String(items.length) },
    { label: 'Searchable products', value: String(products.length) },
    { label: 'Image upload ready', value: selectedImage ? 'Yes' : 'No' },
  ];

  const fetchVariants = async (productId, showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }

    if (!productId) {
      setItems([]);
      setIsLoading(false);
      return;
    }

    try {
      const response = await listAdminVariants(productId);
      setItems(response.items.map(normalizeVariant));
      setSubmitError(null);
    } catch (error) {
      setSubmitError(error);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await listAdminProducts();
      setProducts(response.items.map(normalizeProduct));
    } catch (error) {
      setSubmitError((current) => current || error);
      setProducts([]);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (formValues.product_id) {
      fetchVariants(formValues.product_id);
    } else {
      setItems([]);
      setIsLoading(false);
    }
  }, [formValues.product_id]);

  const handleFieldChange = (field) => (event) => {
    setFormValues((current) => ({
      ...current,
      [field]: event.target.value,
    }));

    setFieldErrors((current) => ({
      ...current,
      [field]: '',
    }));

    setSubmitError(null);
    setSubmitSuccess('');
  };

  const handleProductChange = (_, option) => {
    setFormValues((current) => ({
      ...current,
      product_id: option ? String(option.product_id) : '',
    }));

    setFieldErrors((current) => ({
      ...current,
      product_id: '',
    }));

    setSubmitError(null);
    setSubmitSuccess('');
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedImage(file);
    setSelectedImageName(file ? file.name : '');
    setFieldErrors((current) => ({
      ...current,
      image: '',
    }));
    setSubmitError(null);
    setSubmitSuccess('');
  };

  const resetForm = () => {
    setFormValues(initialFormValues);
    setEditingId(null);
    setFieldErrors({});
    setSelectedImage(null);
    setSelectedImageName('');
    setExistingImageLabel('');
  };

  const validateForm = () => {
    const errors = {};

    if (!formValues.product_id) {
      errors.product_id = 'Please search and select a product.';
    }

    if (!formValues.variant_name.trim()) {
      errors.variant_name = 'Variant name is required.';
    }

    if (!formValues.sku.trim()) {
      errors.sku = 'SKU is required.';
    }

    if (!formValues.price.trim()) {
      errors.price = 'Please enter the variant price.';
    }

    if (!formValues.mrp.trim()) {
      errors.mrp = 'Please enter the variant MRP.';
    }

    if (!formValues.weight.trim()) {
      errors.weight = 'Please enter the variant weight.';
    }

    // if (!editingId && !selectedImage) {
    //   errors.image = 'Please choose a variant image file.';
    // }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setSubmitError({
        message: 'Please review the variant form before submitting.',
      });
      return;
    }

    setIsSaving(true);
    setSubmitError(null);
    setSubmitSuccess('');
    setFieldErrors({});

    try {
      const payload = {
        ...formValues,
        image: selectedImage,
      };

      if (editingId) {
        const result = await updateAdminVariant({
          variant_id: editingId,
          ...payload,
        });
        setSubmitSuccess(result?.message || 'Variant updated successfully.');
      } else {
        const result = await createAdminVariant(payload);
        setSubmitSuccess(result?.message || 'Variant created successfully.');
      }

      resetForm();
      await fetchVariants(formValues.product_id, false);
    } catch (error) {
      setSubmitError(error);
      setFieldErrors(error.validationErrors || {});
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.variant_id);
    setFormValues({
      product_id: String(item.product_id || ''),
      variant_name: item.variant_name || '',
      sku: item.sku || '',
      price: item.price || '',
      mrp: item.mrp || '',
      weight: item.weight || '',
      status: String(item.status ?? '1'),
    });
    setSelectedImage(null);
    setSelectedImageName('');
    setExistingImageLabel(item.image || '');
    setSubmitError(null);
    setSubmitSuccess('');
  };

  const handleDelete = async (item) => {
    setDeletingId(item.variant_id);
    setSubmitError(null);
    setSubmitSuccess('');

    try {
      const result = await deleteAdminVariant(item.variant_id);
      if (editingId === item.variant_id) {
        resetForm();
      }
      setSubmitSuccess(result?.message || 'Variant deleted successfully.');
      await fetchVariants(formValues.product_id || item.product_id, false);
    } catch (error) {
      setSubmitError(error);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Grid container spacing={2.5} className='admin-dashboard-metrics'>
        {headerMetrics.map((metric) => (
          <Grid key={metric.label} size={{ xs: 12, md: 4 }}>
            <Paper className='admin-dashboard-metricCard' elevation={0}>
              <Typography className='admin-dashboard-metricLabel'>
                {metric.label}
              </Typography>
              <Typography className='admin-dashboard-metricValue'>
                {metric.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, xl: 5 }}>
          <Paper
            className='admin-dashboard-featureCard admin-crud-formCard'
            elevation={0}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent='space-between'
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Box>
                <Typography className='admin-dashboard-cardKicker'>
                  {editingId ? 'Edit variant' : 'Create variant'}
                </Typography>
                <Typography className='admin-dashboard-cardTitle'>
                  {editingId ? 'Update variant' : 'Add variant'}
                </Typography>
              </Box>
              <Chip
                label={editingId ? 'Edit mode' : 'Searchable product picker'}
                className='admin-dashboard-statusChip'
              />
            </Stack>

            <Typography className='admin-dashboard-cardText'>
              Search products by name, preview the product image in the picker,
              and submit only the selected `product_id` to the backend.
            </Typography>

            {submitError ? (
              <Alert severity='error' className='auth-alert'>
                {submitError.message}
              </Alert>
            ) : null}

            {submitSuccess ? (
              <Alert severity='success' className='auth-alert'>
                {submitSuccess}
              </Alert>
            ) : null}

            <Box
              component='form'
              onSubmit={handleSubmit}
              className='admin-crud-form'
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    options={products}
                    value={selectedProduct}
                    onChange={handleProductChange}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) =>
                      String(option.product_id) === String(value.product_id)
                    }
                    renderOption={(props, option) => (
                      <Box component='li' {...props}>
                        <Avatar
                          src={buildProductImageUrl(option.image)}
                          alt={option.name}
                          sx={{ width: 40, height: 40, mr: 1.5 }}
                        />
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>
                            {option.name}
                          </Typography>
                          <Typography variant='body2' sx={{ color: '#7a5a49' }}>
                            Product ID: {option.product_id}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Search product'
                        placeholder='Search by product name'
                        error={Boolean(fieldErrors.product_id)}
                        helperText={fieldErrors.product_id}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label='Variant name'
                    value={formValues.variant_name}
                    onChange={handleFieldChange('variant_name')}
                    error={Boolean(fieldErrors.variant_name)}
                    helperText={fieldErrors.variant_name}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='SKU'
                    value={formValues.sku}
                    onChange={handleFieldChange('sku')}
                    error={Boolean(fieldErrors.sku)}
                    helperText={fieldErrors.sku}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='Weight'
                    value={formValues.weight}
                    onChange={handleFieldChange('weight')}
                    error={Boolean(fieldErrors.weight)}
                    helperText={fieldErrors.weight}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='Price'
                    value={formValues.price}
                    onChange={handleFieldChange('price')}
                    error={Boolean(fieldErrors.price)}
                    helperText={fieldErrors.price}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='MRP'
                    value={formValues.mrp}
                    onChange={handleFieldChange('mrp')}
                    error={Boolean(fieldErrors.mrp)}
                    helperText={fieldErrors.mrp}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label='Status'
                    value={formValues.status}
                    onChange={handleFieldChange('status')}
                  >
                    <MenuItem value='1'>Active</MenuItem>
                    <MenuItem value='0'>Inactive</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    component='label'
                    variant='outlined'
                    startIcon={<UploadFileRoundedIcon />}
                    className='admin-crud-secondaryButton'
                    sx={{ width: '100%', justifyContent: 'center' }}
                  >
                    {selectedImageName || 'Choose variant image file'}
                    <input
                      type='file'
                      accept='image/*'
                      hidden
                      onChange={handleImageChange}
                    />
                  </Button>
                  {existingImageLabel && !selectedImageName ? (
                    <Typography className='admin-dashboard-note' sx={{ mt: 1 }}>
                      Current image: {existingImageLabel}
                    </Typography>
                  ) : null}
                  {fieldErrors.image ? (
                    <Typography
                      color='error'
                      sx={{ mt: 1, fontSize: '0.75rem' }}
                    >
                      {fieldErrors.image}
                    </Typography>
                  ) : null}
                </Grid>
              </Grid>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                className='admin-crud-actions'
              >
                <Button
                  type='submit'
                  variant='contained'
                  startIcon={
                    editingId ? <SaveRoundedIcon /> : <AddRoundedIcon />
                  }
                  className='admin-crud-primaryButton'
                  disabled={isSaving}
                >
                  {isSaving
                    ? editingId
                      ? 'Updating...'
                      : 'Creating...'
                    : editingId
                      ? 'Update variant'
                      : 'Add variant'}
                </Button>
                <Button
                  type='button'
                  variant='outlined'
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={resetForm}
                  className='admin-crud-secondaryButton'
                >
                  Reset
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, xl: 7 }}>
          <Paper
            className='admin-dashboard-sideCard admin-crud-tableCard'
            elevation={0}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent='space-between'
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Box>
                <Typography className='admin-dashboard-cardKicker'>
                  Live records
                </Typography>
                <Typography className='admin-dashboard-cardTitle'>
                  {section.label} list
                </Typography>
              </Box>
              <Chip
                label={
                  !formValues.product_id
                    ? 'Select product'
                    : isLoading
                      ? 'Loading...'
                      : `${items.length} rows`
                }
                className='admin-dashboard-statusChip'
              />
            </Stack>

            <Typography className='admin-dashboard-note'>
              Variant creation uses a searchable product selector with image
              preview, but the backend payload still sends only `product_id`.
              The list API is loaded by the currently selected product.
            </Typography>
            <Divider className='admin-dashboard-divider' />

            <TableContainer className='admin-crud-tableWrap'>
              <Table className='admin-crud-table'>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    {visibleColumns.map((column) => (
                      <TableCell key={column.key}>{column.label}</TableCell>
                    ))}
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length + 2}>
                        <Typography className='admin-dashboard-note'>
                          Loading variants...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {!isLoading &&
                    items.map((item) => (
                      <TableRow key={item.variant_id} hover>
                        <TableCell>{item.variant_id}</TableCell>
                        {visibleColumns.map((column) => (
                          <TableCell key={column.key}>
                            {column.key === 'status'
                              ? String(item.status) === '1'
                                ? 'Active'
                                : 'Inactive'
                              : item[column.key] || '-'}
                          </TableCell>
                        ))}
                        <TableCell align='right'>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            justifyContent='flex-end'
                            className='admin-crud-rowActions'
                          >
                            <Button
                              size='small'
                              variant='text'
                              startIcon={<EditRoundedIcon />}
                              onClick={() => handleEdit(item)}
                            >
                              Edit
                            </Button>
                            <Button
                              size='small'
                              color='error'
                              variant='text'
                              startIcon={<DeleteOutlineRoundedIcon />}
                              onClick={() => handleDelete(item)}
                              disabled={deletingId === item.variant_id}
                            >
                              {deletingId === item.variant_id
                                ? 'Deleting...'
                                : 'Delete'}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}

                  {!isLoading && !formValues.product_id ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length + 2}>
                        <Typography className='admin-dashboard-note'>
                          Search and select a product to load its variants.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {!isLoading && formValues.product_id && items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length + 2}>
                        <Typography className='admin-dashboard-note'>
                          No variants returned by the API yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>

            {selectedProduct?.image ? (
              <Box
                component='a'
                href={buildProductImageUrl(selectedProduct.image)}
                target='_blank'
                rel='noreferrer'
                sx={{
                  mt: 2,
                  display: 'inline-flex',
                  color: '#8d4314',
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                View selected product image
              </Box>
            ) : null}

            {existingImageLabel ? (
              <Box
                component='a'
                href={buildVariantImageUrl(existingImageLabel)}
                target='_blank'
                rel='noreferrer'
                sx={{
                  mt: 1.5,
                  display: 'inline-flex',
                  color: '#8d4314',
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                View current variant image
              </Box>
            ) : null}
          </Paper>
        </Grid>
      </Grid>

      {section.stats?.length ? (
        <Box className='admin-dashboard-pillGrid'>
          {section.stats.map((item) => (
            <Box key={item} className='admin-dashboard-pill'>
              {item}
            </Box>
          ))}
        </Box>
      ) : null}
    </>
  );
}
